/* eslint-disable max-len */
import {
  Checkbox,
  formatPercent,
  investmentFrequencies,
  liquidityNeeds,
  normalizePercent,
  RadioButtonGroup,
  required,
  riskTolerances,
  TextField,
  yearsOfExperience,
} from 'entoro-shared';
import React from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';

const checkboxLabels = {
  investmentDecisionConsultProfessional:
    'I consult with my broker, investment adviser, CPA, or other financial professional.',
  investmentDecisionConsultSelf:
    'I generally make my own decisions and/or consult with my co-Subscriber(s).',
  investmentDecisionConsultFamilyFriends:
    'I discuss investment decisions with family and/or friends.',
  investmentSelectionsConfirmed:
    'By checking this box, I hereby affirm that my answers to the forgoing are true and correct.',
};

const InvestorSuitabilityForm = reduxForm({
  form: 'investorSuitability',
  enableReinitialize: true,
})(({ handleSubmit, submitting, update, hasSubmitted }) => (
  <div className='w-clearfix w-form'>
    <form disabled={hasSubmitted} noValidate onSubmit={handleSubmit(update)}>
      <div className='content-spacer'>
        <div className='column-fields w-row'>
          <div className='column-4 w-col w-col-6'>
            <Field
              className='checkbox-field w-checkbox'
              component={RadioButtonGroup}
              disabled={hasSubmitted}
              validate={[required]}
              name='liquidityNeeds'
              label='Liquidity Needs'
              helperText='The ability to quickly and easily convert to cash all or a portion of this investment without experiencing significant loss in value from, for example, the lack of a ready market, or incurring significant costs or penalties is:'
              options={liquidityNeeds.options}
              required
            />
          </div>
          <div className='column-5 w-col w-col-6'>
            <Field
              className='checkbox-field w-checkbox'
              component={RadioButtonGroup}
              disabled={hasSubmitted}
              validate={[required]}
              name='riskTolerance'
              label='Risk Tolerance'
              helperText='Please select the degree of risk you (and any co-Subscribers, if applicable) are willing to take with this investment.'
              options={riskTolerances.options}
              required
            />
          </div>
        </div>
      </div>
      <div className='column-fields w-row'>
        <div className='column-4 w-col w-col-6'>
          <Field
            className='checkbox-field w-checkbox'
            component={RadioButtonGroup}
            disabled={hasSubmitted}
            validate={[required]}
            name='yearsOfExperience'
            label='Years of Experience'
            helperText='How many years’ experience do you have investing in private placements and/or alternative investments (e.g., structured products, hedge funds)?'
            options={yearsOfExperience.options}
            required
          />
        </div>
        <div className='column-5 w-col w-col-6'>
          <Field
            className='checkbox-field w-checkbox'
            component={RadioButtonGroup}
            disabled={hasSubmitted}
            validate={[required]}
            name='investmentFrequency'
            label='Investment Frequency'
            helperText='How many such investments do you make per year?'
            options={investmentFrequencies.options}
            required
          />
        </div>
      </div>
      <div className='spacer'>
        <Field
          className='checkbox-field w-checkbox'
          component={TextField}
          disabled={hasSubmitted}
          validate={[required]}
          name='investmentAllocation'
          label='Investment Allocation (%)'
          helperText='Please estimate the percentage of your total portfolio (excluding personal residence) that would be allocated to private placements as a result of this transaction.'
          required
          normalize={normalizePercent}
          format={formatPercent}
        />
      </div>
      <div className='content-spacer'>
        <div className='_26-black'>
          Prior to making an investment decision (check all that apply):
        </div>
        <div className='column-fields w-row'>
          <div className='w-col w-col-7'>
            <div className='checkbox-field w-checkbox'>
              <Field
                component={Checkbox}
                disabled={hasSubmitted}
                name='investmentDecisionConsultProfessional'
                label={checkboxLabels.investmentDecisionConsultProfessional}
              />
            </div>
            <div className='checkbox-field w-checkbox'>
              <Field
                component={Checkbox}
                disabled={hasSubmitted}
                name='investmentDecisionConsultSelf'
                label={checkboxLabels.investmentDecisionConsultSelf}
              />
            </div>
            <div className='checkbox-field w-checkbox'>
              <Field
                component={Checkbox}
                disabled={hasSubmitted}
                name='investmentDecisionConsultFamilyFriends'
                label={checkboxLabels.investmentDecisionConsultFamilyFriends}
              />
            </div>
          </div>
          <div className='w-col w-col-5' />
        </div>
      </div>
      <div className='_18-reg'>
        By signing this Questionnaire, you affirm that you have received and
        read this Questionnaire and any supplemental documents governing this
        relationship. You affirm that the information you have provided is
        accurate and you agree to notify us of any changes in the information
        provided.
      </div>
      <div className='checkbox-field w-checkbox'>
        <Field
          component={Checkbox}
          disabled={hasSubmitted}
          validate={[required]}
          name='investmentSelectionsConfirmed'
          label={checkboxLabels.investmentSelectionsConfirmed}
        />
      </div>
      <div className='grey-regular notice'>
        Notice regarding our Customer Identification Program (CIP). To help the
        government fight the funding of terrorism and money laundering
        activities, federal law requires financial institutions to obtain,
        verify, and record information that identifies each person who opens an
        account (for purposes of CIP, an account is a formal relationship with a
        broker-dealer established to effect transactions in securities).
        <br />‍<br />
        When you open an account, we are required to collect information such as
        your name, date of birth, address, Identification Number (U.S. Citizen:
        taxpayer identification number (social security number or employer
        identification number); Non-U.S. Citizen: taxpayer identification
        number, passport number, and country of issuance, alien identification
        card number, or government issued identification showing nationality,
        residence, and a photograph of you.) You may also need to show your
        driver&apos;s license or other identifying documents.
        <br />‍<br />A corporation, partnership, trust or other legal entity may
        need to provide other information, such as its principal place of
        business, local office, employer identification number, certified
        articles of incorporation, government-issued business license, a
        partnership agreement or a trust agreement.
        <br />‍<br />
        U.S. Department of the Treasury, Securities and Exchange Commission, and
        FINRA already require you to provide most of this information. These
        rules also may require you to provide additional information, such as
        your net worth, annual income, occupation, employment information,
        investment experience and objectives, and risk tolerance.
        <br />‍<br />
        The information collected about you may be shared with third-party
        vendors and service providers who perform services for us, such as
        identification verification, fraud prevention, advertising, mailing
        services, tax and accounting services, contest fulfillment, web hosting,
        and analytics services. 
        <br />‍<br />
        If you have any questions, please contact us at {' '}
        <a href='mailto:support@offerboard.com'>support@offerboard.com</a>.
      </div>
      <div className='full-line bottom' />
      {!hasSubmitted ? (
        <input
          type='submit'
          value='Save & Continue'
          disabled={submitting}
          className={`button-2 w-button ${submitting ? 'btn-disabled' : ''}`}
        />
      ) : null}
    </form>
  </div>
));

const mapStateToProps = ({ investorUser }) => ({
  initialValues: investorUser.suitability || {
    investmentDecisionConsultProfessional: false,
    investmentDecisionConsultSelf: false,
    investmentDecisionConsultFamilyFriends: false,
    investmentSelectionsConfirmed: false,
  },
  hasSubmitted: Boolean(investorUser.suitability),
});

export default connect(mapStateToProps)(InvestorSuitabilityForm);



// WEBPACK FOOTER //
// ./src/components/Account/InvestorSuitability/InvestorSuitabilityForm.js